import { Grid, Group, Stack, Text, Table, useMantineTheme } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { InformationRequestStatus } from "@tfg/db"
import { Button, EqualWidthGrid, Loader, ScrollArea } from "@tfg/ui"
import _ from "lodash"
import React, { useMemo, useCallback, useState } from "react"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"

import { FindOperationChainsResponseDto } from "~/gen/api"
import { ReopenRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/ReopenRequestModal.tsx"
import { TraceabilityRequestItem } from "~/pages/SupplierPortal/TraceabilityRequests/components/TraceabilityRequestItem.tsx"
import {
  useGetTraceabilityRequest,
  useSetStatusRequestMutation,
} from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import { InformationRequestContextProvider } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import {
  OperationItem,
  requestsToRequestsWithOp,
  RequestWithOpItem,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context.tsx"

import { InformationRequestSummary } from "./InformationRequestSummary"

interface TraceabilityRequestProps {
  initialData: RequestWithOpItem
  productionSites: SupplierResponses.ProductionSite[] | undefined
  status: InformationRequestStatus
  operationChains: FindOperationChainsResponseDto[]
}

export const TraceabilityRequest = React.memo(
  ({ initialData, status, operationChains }: TraceabilityRequestProps) => {
    const { isAdmin } = useAuth()
    const { colors } = useMantineTheme()

    const [reopened, reopenHandlers] = useDisclosure(false)

    const { mutate: setStatusOpen } = useSetStatusRequestMutation()
    const [reopenRequestMode, setReopenRequestMode] = useState<
      "clean" | "dirty"
    >("clean")

    const { data, isLoading } = useGetTraceabilityRequest(
      initialData.original.id,
      initialData.original
    )

    const request = useMemo(() => {
      if (!isLoading && data) {
        return requestsToRequestsWithOp({
          requests: [data],
          operationChains,
        })[0]
      } else {
        return initialData
      }
    }, [isLoading, data, operationChains, initialData])
    const operationItems = request.opItems

    const ranks = Object.keys(request.opItemsByRank)
    const rows = useMemo(() => buildRows(operationItems), [operationItems])

    const handleSetStatusOpen = useCallback(
      (newStatus: InformationRequestStatus) => {
        setStatusOpen({
          requestId: request.original.id,
          status: newStatus,
        })
      },
      [request.original.id, setStatusOpen]
    )

    const handleReopenClick = useCallback(
      (mode: "clean" | "dirty") => {
        setReopenRequestMode(mode)
        reopenHandlers.open()
      },
      [reopenHandlers]
    )

    const adminButtons = useMemo(() => {
      if (!isAdmin) return null
      return status === InformationRequestStatus.OPENED ? (
        <Stack>
          <EqualWidthGrid>
            <Button
              onClick={() => handleSetStatusOpen("CLOSED")}
              variant="admin"
              fullWidth={true}
              size="xs"
            >
              CLOSE
            </Button>
            <Button
              onClick={() => handleSetStatusOpen("CANCELED")}
              variant="admin"
              size="xs"
              fullWidth={true}
            >
              CANCEL
            </Button>
          </EqualWidthGrid>
        </Stack>
      ) : (
        <Group>
          <Button onClick={() => handleReopenClick("clean")} variant="admin">
            <Text>REOPEN & CANCEL CHILDREN</Text>
          </Button>
          <Button
            onClick={() => handleReopenClick("dirty")}
            variant="admin-tertiary"
          >
            REOPEN
          </Button>
          <ReopenRequestModal
            requestId={request.original.id}
            mode={reopenRequestMode}
            opened={reopened}
            onClose={reopenHandlers.close}
          />
        </Group>
      )
    }, [
      handleReopenClick,
      handleSetStatusOpen,
      isAdmin,
      reopenHandlers.close,
      reopenRequestMode,
      reopened,
      request.original.id,
      status,
    ])
    const requestItems = useMemo(
      () =>
        rows.map((row, index) => (
          <TraceabilityRequestItem
            key={`${request.original.id}-row-${index}`}
            index={index}
            operationsItemWithRowspan={row}
          />
        )),
      [rows, request]
    )

    if (isLoading) return <Loader fullPage />

    return (
      <InformationRequestContextProvider request={request} status={status}>
        <Grid style={{ border: "1px solid", borderColor: colors.gray[3] }}>
          <Grid.Col
            span={3}
            bg="gray.0"
            style={{ borderRight: "1px solid", borderColor: colors.gray[3] }}
          >
            <InformationRequestSummary adminButtons={adminButtons} />
          </Grid.Col>
          <Grid.Col span={9} bg="white">
            <ScrollArea
              showEdgeFade
              type="auto"
              w="100%"
              h="70vh"
              zoomable
              hideScrollbar
              dragScroll
            >
              <Table
                style={{
                  borderCollapse: "collapse",
                  border: "none",
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    {ranks.map((rank) => (
                      <td
                        key={`${request.original.id}-${rank}`}
                        style={{ width: `calc(100%/${ranks.length})` }}
                      />
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {request.error && isAdmin && (
                    <Table.Tr>
                      <Table.Td>
                        <Text c="violet">{request.error}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {requestItems}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Grid.Col>
        </Grid>
      </InformationRequestContextProvider>
    )
  }
)

function buildRows(operationItems: OperationItem[]) {
  const rootNodes = operationItems.filter((oi) => !oi.parentNodeId)
  if (rootNodes.length !== 1) {
    return []
  }
  const rootNode = rootNodes[0]
  return zipNextRow(rootNode, operationItems)
}

function zipNextRow(
  curnode: OperationItem,
  nodes: OperationItem[]
): ({ op: OperationItem; rowspan: number } | "no-op")[][] {
  const children = nodes.filter((n) => n.parentNodeId === curnode.nodeId)
  if (children.length === 0) {
    return [[{ op: curnode, rowspan: 1 }]]
  }

  const allchild = children.flatMap((child) => zipNextRow(child, nodes))
  const rowspan = allchild.length
  const zipped = _.zip([[{ op: curnode, rowspan }]], allchild)
  return zipped.map((rank) => rank.flatMap((y) => y || ["no-op"]))
}
